/**
 * Pixel letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pixelLettersPack: ToilePack = {
    slug: 'pixel-letters',
    category: 'illustration',
    name: 'Pixel letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'alphabet', label: '5x7 alphabet', subject: 'a chart of the 26 capitals "A" to "Z" as 5 by 7 pixel letters on a faint square grid, five rows, every square crisp'},
        {key: 'numbers', label: 'Pixel numbers', subject: 'a chart of the digits "0" to "9" and the signs "!" "?" "+" as 5 by 7 pixel characters on a square grid'},
        {key: 'title', label: 'Game title', subject: 'the words "GAME OVER" in large pixel letters with a one-pixel dark outline, centered on the page'},
    ],
    styles: [
        {key: 'mono', label: 'Black pixels', style: 'black square pixels, hard edges and a faint light gray grid', palette: 'Black pixels only, with a pale gray grid on a white background.', framing: 'The sheet fills the page in even rows with a narrow margin.'},
        {key: 'arcade', label: 'Arcade', style: 'bright 8-bit arcade colors with a one-pixel dark outline and a flat highlight', palette: 'Yellow, cyan and magenta pixels on a solid black background.', framing: 'The sheet fills the page in even rows with a narrow margin.'},
        {key: 'beads', label: 'Fuse beads', style: 'square fuse beads, each pixel a flat bead with a small center hole', palette: 'Red, blue, yellow and green beads on a white pegboard background.', framing: 'The sheet fills the page in even rows with a narrow margin.'},
    ],
    directions: ['lowercase', 'blue pixels', 'hearts'],
    directionHint: 'try: lowercase',
    seo: {
        title: 'Pixel Letters: 8-Bit Alphabet, Numbers, Game Title',
        metaDescription: 'Nine pixel lettering sheets: a 5 by 7 alphabet, digits and a game title, in black pixels, arcade colors and fuse beads.',
        h1: 'Pixel letters: an 8-bit alphabet, pixel numbers, a game title',
        tagline: 'Blocky letters on a square grid, in three pixel treatments.',
        intro: 'Pixel letters are blocky 8-bit letters for game titles, cross-stitch, perler beads and Minecraft builds. The nine sheets give a 5 by 7 alphabet on a square grid, the digits 0 to 9 with a few signs, and the title GAME OVER in large pixels. Each comes in black squares, bright arcade colors, or square fuse beads. Ask for lowercase, blue pixels or hearts.',
        useCases: [
            'a cross-stitch chart',
            'a perler bead pattern',
            'a game title',
            'a Minecraft build reference',
        ],
        faq: [
            {q: 'How big is each letter?', a: 'Each letter is 5 pixels wide and 7 pixels tall.'},
            {q: 'Can I use these for cross-stitch or perler beads?', a: 'The fuse-bead style shows each pixel as a square bead with a small center hole. The grid is the chart to copy from.'},
            {q: 'Which signs are included?', a: 'The digits sheet also has an exclamation mark, a question mark and a plus sign.'},
            {q: 'Can I get a different title?', a: 'Yes. Ask for another title, lowercase or hearts, and it is set on the same grid.'},
        ],
    },
}
